import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  deleteFirewallInstance,
  fetchFirewallDetails,
  fetchFwCfg,
  updateFwCfg,
} from "./firewallThunks";
import {
  MinioBucketRule,
  MinioFirewallConfig,
  MinioFirewallConfigData,
} from "../../api/firewallApi";
import { LoadingStatus } from "common/types";
import { SupportedFirewallAPIs } from "./utils";

export interface DeploymentsState {
  firewallCfg: MinioFirewallConfigData | null;
  status: LoadingStatus;
  details: MinioFirewallConfig | null;
  loadingFirewallDetails: boolean;
  openDeleteFirewallModal: boolean;
  loadingDeleteFirewall: boolean;
  firewallDeleted: boolean;
  editMode: boolean;
  loadingSaveFirewallRule: boolean;
  ruleToEdit: IEditRuleMode;
  ruleFields: MinioBucketRule;
}

export interface IEditRuleMode {
  bucket: string;
  label: string;
  access: string;
  anonymous: string;
  newRule: boolean;
}

export interface RequestLimit {
  field: "api" | "burst" | "rate";
  value: string;
  index: number;
}

const initialState: DeploymentsState = {
  firewallCfg: null,
  status: "idle",
  details: null,
  loadingFirewallDetails: true,
  openDeleteFirewallModal: false,
  loadingDeleteFirewall: false,
  firewallDeleted: false,
  editMode: false,
  loadingSaveFirewallRule: false,
  ruleToEdit: {
    bucket: "",
    label: "",
    access: "",
    anonymous: "",
    newRule: false,
  },
  ruleFields: {
    access: "allow",
    label: "",
    bucket: "",
    anonymous: "deny",
    bandwidth: {
      DownloadRate: "",
      downloadRatePerSec: "",
      UploadRate: "",
      uploadRatePerSec: "",
    },
    requests: [],
  },
};

export const firewallSlice = createSlice({
  name: "firewalls",
  initialState,
  reducers: {
    setStatus: (state, action: PayloadAction<LoadingStatus>) => {
      state.status = action.payload;
    },
    resetFirewallsState: () => {
      return initialState;
    },
    setLoadingFirewallDetails: (state, action: PayloadAction<boolean>) => {
      state.loadingFirewallDetails = action.payload;
    },
    setOpenDeleteFirewall: (state, action: PayloadAction<boolean>) => {
      state.openDeleteFirewallModal = action.payload;
    },
    setFirewallDeleted: (state, action: PayloadAction<boolean>) => {
      state.firewallDeleted = action.payload;
    },
    setFirewallRuleEditMode: (state, action: PayloadAction<IEditRuleMode>) => {
      state.ruleToEdit = action.payload;
      state.editMode = true;
    },
    setFirewallEditMode: (state, action: PayloadAction<boolean>) => {
      state.editMode = action.payload;
    },
    setNewFirewallRule: (state) => {
      state.ruleToEdit = {
        newRule: true,
        label: "",
        bucket: "",
        access: "",
        anonymous: "",
      };
      state.editMode = true;
    },
    setInitialRuleFields: (state, action: PayloadAction<MinioBucketRule>) => {
      state.ruleFields = action.payload;
    },
    addRequestLine: (state) => {
      const currRequests = state.ruleFields?.requests || [];
      state.ruleFields.requests = [
        ...currRequests,
        { api: SupportedFirewallAPIs[0].value, burst: "1", rate: "1" },
      ];
    },
    removeRequestLine: (state, action: PayloadAction<number>) => {
      state.ruleFields.requests =
        state.ruleFields.requests?.filter(
          (_, index) => index !== action.payload,
        ) || [];
    },
    setFwAccess: (state, action: PayloadAction<string>) => {
      state.ruleFields.access = action.payload;
    },
    setFwLabel: (state, action: PayloadAction<string>) => {
      state.ruleFields.label = action.payload;
    },
    setFwBucket: (state, action: PayloadAction<string>) => {
      state.ruleFields.bucket = action.payload;
    },
    setFwAnonymous: (state, action: PayloadAction<string>) => {
      state.ruleFields.anonymous = action.payload;
    },
    setFwBandwidthDownloadRate: (state, action: PayloadAction<string>) => {
      if (state.ruleFields.bandwidth) {
        state.ruleFields.bandwidth.DownloadRate = action.payload;
      }
    },
    setFwBandwidthDownloadRatePerSec: (
      state,
      action: PayloadAction<string>,
    ) => {
      if (state.ruleFields.bandwidth) {
        state.ruleFields.bandwidth.downloadRatePerSec = action.payload;
      }
    },
    setFwBandwidthUploadRate: (state, action: PayloadAction<string>) => {
      if (state.ruleFields.bandwidth) {
        state.ruleFields.bandwidth.UploadRate = action.payload;
      }
    },
    setFwBandwidthUploadRatePerSec: (state, action: PayloadAction<string>) => {
      if (state.ruleFields.bandwidth) {
        state.ruleFields.bandwidth.uploadRatePerSec = action.payload;
      }
    },
    setRequestLimit: (state, action: PayloadAction<RequestLimit>) => {
      const itemEdit = state.ruleFields.requests;

      if (itemEdit && itemEdit[action.payload.index]) {
        const fullElement = itemEdit[action.payload.index];

        switch (action.payload.field) {
          case "api":
            fullElement.api = action.payload.value;
            break;
          case "burst":
            fullElement.burst = action.payload.value;
            break;
          case "rate":
            fullElement.rate = action.payload.value;
            break;
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchFwCfg.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchFwCfg.fulfilled, (state, action) => {
        state.status = "idle";
        if (action.payload) {
          if (action.payload.configData) {
            state.firewallCfg = action.payload.configData;
          }
        }
      })
      .addCase(fetchFwCfg.rejected, (state, action) => {
        state.status = "failed";
      })
      // Fetch Firewall Details
      .addCase(fetchFirewallDetails.pending, (state) => {
        state.loadingFirewallDetails = true;
      })
      .addCase(fetchFirewallDetails.fulfilled, (state, action) => {
        state.loadingFirewallDetails = false;
        state.details = action.payload;
      })
      .addCase(fetchFirewallDetails.rejected, (state) => {
        state.loadingFirewallDetails = false;
        state.details = null;
      })
      // Delete Firewall Instance
      .addCase(deleteFirewallInstance.pending, (state) => {
        state.loadingDeleteFirewall = true;
      })
      .addCase(deleteFirewallInstance.fulfilled, (state) => {
        state.loadingDeleteFirewall = false;
        state.firewallDeleted = true;
      })
      .addCase(deleteFirewallInstance.rejected, (state) => {
        state.loadingDeleteFirewall = false;
      })
      // Add / Edit Firewall
      .addCase(updateFwCfg.pending, (state) => {
        state.loadingSaveFirewallRule = true;
      })
      .addCase(updateFwCfg.fulfilled, (state) => {
        state.loadingSaveFirewallRule = false;
        state.editMode = false;
        state.ruleToEdit = {
          bucket: "",
          label: "",
          access: "",
          anonymous: "",
          newRule: false,
        };
      })
      .addCase(updateFwCfg.rejected, (state) => {
        state.loadingSaveFirewallRule = false;
      });
  },
});

export const {
  setStatus,
  resetFirewallsState,
  setLoadingFirewallDetails,
  setOpenDeleteFirewall,
  setFirewallDeleted,
  setFirewallRuleEditMode,
  setFirewallEditMode,
  setNewFirewallRule,
  setInitialRuleFields,
  addRequestLine,
  removeRequestLine,
  setFwAccess,
  setFwLabel,
  setFwBucket,
  setFwAnonymous,
  setFwBandwidthDownloadRate,
  setFwBandwidthDownloadRatePerSec,
  setFwBandwidthUploadRate,
  setFwBandwidthUploadRatePerSec,
  setRequestLimit,
} = firewallSlice.actions;

export default firewallSlice.reducer;
